import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import EntityService from '../../services/EntityService';
import { API } from '../../shared/api/Api';

/**
 * Fetch all instructors
 */
export const fetchInstructors = createAsyncThunk(
  'instructors/fetchInstructors',
  async ({ token, params }, { rejectWithValue }) => {
    try {
      const response = await EntityService.getAllEnties(
        token,
        API.Instructors.app_instructors_all,
        params
      );
      if (response.data.status === 200 && response.data.errorCode === 0) {
        return {
          items: response.data.data.items,
          currentPage: response.data.data.current_page,
          totalPages: response.data.data.last_page,
          total: response.data.data.total,
        };
      }
      return rejectWithValue(response.data.message || 'Failed to fetch instructors');
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch instructors');
    }
  }
);

/**
 * Fetch a single instructor by ID
 */
export const fetchInstructorById = createAsyncThunk(
  'instructors/fetchInstructorById',
  async ({ token, instructorId }, { rejectWithValue }) => {
    try {
      const response = await EntityService.getEntity(
        token,
        API.Instructors.app_instructors_get,
        instructorId
      );
      if (response.data.status === 200 && response.data.errorCode === 0) {
        return response.data.data;
      }
      return rejectWithValue(response.data.message || 'Failed to fetch instructor');
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch instructor');
    }
  }
);

/**
 * Create a new instructor
 */
export const createInstructor = createAsyncThunk(
  'instructors/createInstructor',
  async ({ token, formData }, { rejectWithValue }) => {
    try {
      const data = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        if (key === 'present') {
          data.append('present', value ? 1 : 0);
        } else {
          data.append(key, value);
        }
      });

      const response = await EntityService.addEntity(
        token,
        API.Instructors.app_instructors_add,
        data
      );
      if (response.data.status === 200 || response.data.status === 201) {
        return response.data.data;
      }
      return rejectWithValue(response.data.message || 'Failed to create instructor');
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to create instructor';
      const validationErrors = error.response?.data?.data || {};
      return rejectWithValue({ message: errorMessage, errors: validationErrors });
    }
  }
);

/**
 * Update an existing instructor
 */
export const updateInstructor = createAsyncThunk(
  'instructors/updateInstructor',
  async ({ token, instructorId, formData, initialData }, { rejectWithValue }) => {
    try {
      const data = new FormData();
      
      // Only append changed fields
      Object.keys(formData).forEach((key) => {
        const currentValue = formData[key];
        const initialValue = initialData[key];

        // Handle file inputs differently
        if (currentValue instanceof File) {
          data.append(key, currentValue);
        }
        // Handle present toggle
        else if (key === 'present') {
          if (currentValue !== initialValue) {
            data.append('present', currentValue ? 1 : 0);
          }
        }
        // Handle other changed fields
        else if (currentValue !== initialValue) {
          data.append(key, currentValue);
        }
      });

      const response = await EntityService.updateEntity(
        token,
        API.Instructors.app_instructors_update,
        instructorId,
        data
      );
      if (response.data.status === 200) {
        return response.data.data;
      }
      return rejectWithValue(response.data.message || 'Failed to update instructor');
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to update instructor';
      const validationErrors = error.response?.data?.data || {};
      return rejectWithValue({ message: errorMessage, errors: validationErrors });
    }
  }
);

/**
 * Delete an instructor
 */
export const deleteInstructor = createAsyncThunk(
  'instructors/deleteInstructor',
  async ({ token, instructorId }, { rejectWithValue }) => {
    try {
      const response = await EntityService.deleteEntity(
        token,
        API.Instructors.app_instructors_delete,
        instructorId
      );
      if (response.data.status === 200) {
        return instructorId;
      }
      return rejectWithValue(response.data.message || 'Failed to delete instructor');
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete instructor');
    }
  }
);

const initialState = {
  // List state
  entities: [],
  pagination: {
    currentPage: 1,
    totalPages: 1,
    total: 0,
  },
  
  // Selected entity state
  selectedEntity: null,
  initialFormData: null,
  
  // Status states
  listStatus: 'idle',
  detailStatus: 'idle',
  createStatus: 'idle',
  updateStatus: 'idle',
  deleteStatus: 'idle',
  
  // Error states
  listError: null,
  detailError: null,
  createError: null,
  updateError: null,
  deleteError: null,
  
  // Form validation errors
  formErrors: {},
};

const instructorCrudSlice = createSlice({
  name: 'instructors',
  initialState,
  reducers: {
    clearSelectedEntity: (state) => {
      state.selectedEntity = null;
      state.initialFormData = null;
      state.detailStatus = 'idle';
      state.detailError = null;
      state.formErrors = {};
    },
    
    clearFormErrors: (state) => {
      state.formErrors = {};
      state.createError = null;
      state.updateError = null;
    },
    
    resetSubmissionStatus: (state) => {
      state.createStatus = 'idle';
      state.updateStatus = 'idle';
      state.createError = null;
      state.updateError = null;
      state.formErrors = {};
    },
    
    updateFormData: (state, action) => {
      if (state.selectedEntity) {
        state.selectedEntity = {
          ...state.selectedEntity,
          ...action.payload,
        };
      }
    },
  },
  extraReducers: (builder) => {
    // Fetch instructors list
    builder
      .addCase(fetchInstructors.pending, (state) => {
        state.listStatus = 'loading';
        state.listError = null;
      })
      .addCase(fetchInstructors.fulfilled, (state, action) => {
        state.listStatus = 'succeeded';
        state.entities = action.payload.items;
        state.pagination = {
          currentPage: action.payload.currentPage,
          totalPages: action.payload.totalPages,
          total: action.payload.total,
        };
        state.listError = null;
      })
      .addCase(fetchInstructors.rejected, (state, action) => {
        state.listStatus = 'failed';
        state.listError = action.payload;
      });

    // Fetch instructor by ID
    builder
      .addCase(fetchInstructorById.pending, (state) => {
        state.detailStatus = 'loading';
        state.detailError = null;
      })
      .addCase(fetchInstructorById.fulfilled, (state, action) => {
        state.detailStatus = 'succeeded';
        state.selectedEntity = action.payload;
        state.initialFormData = { ...action.payload };
        state.detailError = null;
        state.formErrors = {};
      })
      .addCase(fetchInstructorById.rejected, (state, action) => {
        state.detailStatus = 'failed';
        state.detailError = action.payload;
      });

    // Create instructor
    builder
      .addCase(createInstructor.pending, (state) => {
        state.createStatus = 'loading';
        state.createError = null;
        state.formErrors = {};
      })
      .addCase(createInstructor.fulfilled, (state) => {
        state.createStatus = 'succeeded';
        state.createError = null;
        state.formErrors = {};
      })
      .addCase(createInstructor.rejected, (state, action) => {
        state.createStatus = 'failed';
        const errorData = action.payload;
        state.createError = errorData.message || 'Failed to create instructor';
        state.formErrors = errorData.errors || {};
      });

    // Update instructor
    builder
      .addCase(updateInstructor.pending, (state) => {
        state.updateStatus = 'loading';
        state.updateError = null;
        state.formErrors = {};
      })
      .addCase(updateInstructor.fulfilled, (state, action) => {
        state.updateStatus = 'succeeded';
        state.selectedEntity = action.payload;
        state.initialFormData = { ...action.payload };
        state.updateError = null;
        state.formErrors = {};
      })
      .addCase(updateInstructor.rejected, (state, action) => {
        state.updateStatus = 'failed';
        const errorData = action.payload;
        state.updateError = errorData.message || 'Failed to update instructor';
        state.formErrors = errorData.errors || {};
      });

    // Delete instructor
    builder
      .addCase(deleteInstructor.pending, (state) => {
        state.deleteStatus = 'loading';
        state.deleteError = null;
      })
      .addCase(deleteInstructor.fulfilled, (state, action) => {
        state.deleteStatus = 'succeeded';
        state.entities = state.entities.filter(instructor => instructor.id !== action.payload);
        state.deleteError = null;
      })
      .addCase(deleteInstructor.rejected, (state, action) => {
        state.deleteStatus = 'failed';
        state.deleteError = action.payload;
      });
  },
});

export const {
  clearSelectedEntity,
  clearFormErrors,
  resetSubmissionStatus,
  updateFormData,
} = instructorCrudSlice.actions;

// Selectors
export const selectInstructors = (state) => state.instructors.entities;
export const selectInstructorsPagination = (state) => state.instructors.pagination;
export const selectSelectedInstructor = (state) => state.instructors.selectedEntity;
export const selectInitialFormData = (state) => state.instructors.initialFormData;
export const selectInstructorsListStatus = (state) => state.instructors.listStatus;
export const selectInstructorDetailStatus = (state) => state.instructors.detailStatus;
export const selectInstructorCreateStatus = (state) => state.instructors.createStatus;
export const selectInstructorUpdateStatus = (state) => state.instructors.updateStatus;
export const selectInstructorDeleteStatus = (state) => state.instructors.deleteStatus;
export const selectInstructorsListError = (state) => state.instructors.listError;
export const selectInstructorDetailError = (state) => state.instructors.detailError;
export const selectInstructorCreateError = (state) => state.instructors.createError;
export const selectInstructorUpdateError = (state) => state.instructors.updateError;
export const selectInstructorDeleteError = (state) => state.instructors.deleteError;
export const selectInstructorFormErrors = (state) => state.instructors.formErrors;

export default instructorCrudSlice.reducer;
